import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { authStore, type DashboardAuth } from '../../auth/authStore';
import {
  CanvassingSessionRecordingPurpose,
  CanvassingSessionRecordingWarning,
} from '../../graphql/__generated__/graphql';
import { readDashboardAccountScope } from '../voters/voterAccountScope';
import {
  fetchCanvassingSessionRecording,
  interpretCanvassingSessionRecording,
  type CanvassingSessionFetchImpl,
  type CanvassingSessionRecordingResult,
  type InterpretedCanvassingSessionRecording,
} from './canvassingSessionApi';

export const SESSION_RECORDING_NOT_MERGED_MESSAGE = 'No merged session recording is available.';
export const SESSION_RECORDING_UNAVAILABLE_MESSAGE = 'The session recording is unavailable.';
export const SESSION_RECORDING_PLAYBACK_ERROR_MESSAGE = 'Unable to load the session recording. Try again.';
export const SESSION_RECORDING_DOWNLOAD_ERROR_MESSAGE = 'Unable to prepare the download. Try again.';
export const SESSION_RECORDING_PLAYBACK_PREPARING_MESSAGE = 'Preparing recording…';
export const SESSION_RECORDING_DOWNLOAD_PREPARING_MESSAGE = 'Preparing download…';
export const SESSION_RECORDING_DOWNLOAD_REQUESTED_MESSAGE = 'Download requested.';
export const SESSION_RECORDING_INCOMPLETE_WARNING =
  'This recording may be incomplete. Missing intervals are omitted from playback and download.';
export const SESSION_RECORDING_COMPLETENESS_UNKNOWN_WARNING =
  'Recording completeness could not be verified. Available audio can still be played or downloaded.';

const HAVE_METADATA = 1;

export const canvassingSessionRecordingFileName = (sessionId: string): string =>
  `canvassing-session-${sessionId}.m4a`;

export type SessionRecordingWarnings = {
  incomplete: boolean;
  completenessUnknown: boolean;
};

const EMPTY_WARNINGS: SessionRecordingWarnings = {
  incomplete: false,
  completenessUnknown: false,
};

export type SessionRecordingPlaybackState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; url: string; actionId: number; restorePosition: boolean }
  | { status: 'error'; message: string }
  | { status: 'media-error' };

export type SessionRecordingDownloadState =
  | { status: 'idle' }
  | { status: 'preparing' }
  | { status: 'requested' }
  | { status: 'error'; message: string };

type RecordingScope = {
  sessionId: string;
  permitted: boolean;
  accountScope: string;
};

export type CanvassingSessionRecordingClient = {
  fetchRecording: (
    input: { sessionId: string; purpose: CanvassingSessionRecordingPurpose },
    options: { signal: AbortSignal },
  ) => Promise<CanvassingSessionRecordingResult>;
};

export type UseCanvassingSessionRecordingOptions = {
  sessionId: string;
  permitted: boolean;
  fetchImpl?: CanvassingSessionFetchImpl;
  api?: CanvassingSessionRecordingClient;
};

export type UseCanvassingSessionRecordingResult = {
  playback: SessionRecordingPlaybackState;
  download: SessionRecordingDownloadState;
  warnings: SessionRecordingWarnings;
  requestPlayback: () => void;
  retryPlayback: () => void;
  requestDownload: () => void;
  reportMediaError: () => void;
  capturePosition: () => void;
  bindAudio: (node: HTMLAudioElement | null) => void;
};

const IDLE_PLAYBACK: SessionRecordingPlaybackState = { status: 'idle' };
const IDLE_DOWNLOAD: SessionRecordingDownloadState = { status: 'idle' };

const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && Reflect.get(error, 'name') === 'AbortError';

const sameScope = (left: RecordingScope, right: RecordingScope): boolean =>
  left.sessionId === right.sessionId && left.permitted === right.permitted && left.accountScope === right.accountScope;

export const mergeSessionRecordingWarnings = (
  current: SessionRecordingWarnings,
  warnings: readonly CanvassingSessionRecordingWarning[],
): SessionRecordingWarnings => ({
  incomplete:
    current.incomplete ||
    warnings.some(
      warning => warning === CanvassingSessionRecordingWarning.Partial || warning === CanvassingSessionRecordingWarning.Gaps,
    ),
  completenessUnknown:
    current.completenessUnknown || warnings.includes(CanvassingSessionRecordingWarning.CompletenessUnknown),
});

const playbackFailureMessage = (result: Exclude<InterpretedCanvassingSessionRecording, { kind: 'ready' }>): string => {
  if (result.kind === 'not-merged') return SESSION_RECORDING_NOT_MERGED_MESSAGE;
  if (result.kind === 'unavailable') return SESSION_RECORDING_UNAVAILABLE_MESSAGE;
  return SESSION_RECORDING_PLAYBACK_ERROR_MESSAGE;
};

const downloadFailureMessage = (result: Exclude<InterpretedCanvassingSessionRecording, { kind: 'ready' }>): string => {
  if (result.kind === 'not-merged') return SESSION_RECORDING_NOT_MERGED_MESSAGE;
  if (result.kind === 'unavailable') return SESSION_RECORDING_UNAVAILABLE_MESSAGE;
  return SESSION_RECORDING_DOWNLOAD_ERROR_MESSAGE;
};

const readFinitePosition = (audio: HTMLAudioElement): number | null => {
  const position = audio.currentTime;
  return Number.isFinite(position) && position >= 0 ? position : null;
};

const disposeAudioElement = (audio: HTMLAudioElement): void => {
  audio.pause();
  audio.removeAttribute('src');
  audio.load();
};

// Signed S3 responses ignore the cross-origin download attribute. The response
// Content-Disposition is authoritative, so this only starts the browser download
// and never reads the recording into a Blob.
const activateDirectDownload = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.append(link);
  try {
    link.click();
  } finally {
    link.remove();
  }
};

const attemptPlayback = (audio: HTMLAudioElement): void => {
  try {
    const playResult: unknown = audio.play();
    if (playResult instanceof Promise) {
      void playResult.catch(() => undefined);
    }
  } catch {
    // A browser may throw while applying its autoplay policy. Native controls stay usable.
  }
};

const restorePlaybackPosition = (audio: HTMLAudioElement, position: number): void => {
  const duration = audio.duration;
  const target = Number.isFinite(duration) && duration >= 0 ? Math.min(position, duration) : position;
  try {
    audio.currentTime = target;
  } catch {
    // Restoring the previous position failed. Native controls remain usable from the beginning.
  }
};

export const useCanvassingSessionRecording = ({
  sessionId,
  permitted,
  fetchImpl,
  api: apiOverride,
}: UseCanvassingSessionRecordingOptions): UseCanvassingSessionRecordingResult => {
  const accountScopeRef = useRef(readDashboardAccountScope(authStore.getAuth()));
  const discardRef = useRef<() => void>(() => undefined);
  const subscribeAuth = useCallback((onStoreChange: () => void) => {
    return authStore.subscribe((nextAuth: DashboardAuth | null) => {
      const nextScope = readDashboardAccountScope(nextAuth);
      if (nextScope !== accountScopeRef.current) {
        accountScopeRef.current = nextScope;
        discardRef.current();
      }
      onStoreChange();
    });
  }, []);
  const auth = useSyncExternalStore(subscribeAuth, authStore.getAuth);
  const accountScope = readDashboardAccountScope(auth);
  const scope: RecordingScope = { sessionId, permitted, accountScope };
  const api = apiOverride ?? null;
  const fetchImplRef = useRef(fetchImpl);
  fetchImplRef.current = fetchImpl;
  const apiRef = useRef(api);
  apiRef.current = api;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playbackControllerRef = useRef<AbortController | null>(null);
  const downloadControllerRef = useRef<AbortController | null>(null);
  const playbackGenerationRef = useRef(0);
  const downloadGenerationRef = useRef(0);
  const scopeEpochRef = useRef(0);
  const playbackInFlightRef = useRef(false);
  const downloadInFlightRef = useRef(false);
  const suppressMediaErrorRef = useRef(false);
  const positionRef = useRef<number | null>(null);
  const scopeRef = useRef(scope);
  const playbackStateRef = useRef<SessionRecordingPlaybackState>(IDLE_PLAYBACK);
  const [trackedScope, setTrackedScope] = useState(scope);
  const [playback, setPlayback] = useState<SessionRecordingPlaybackState>(IDLE_PLAYBACK);
  const [download, setDownload] = useState<SessionRecordingDownloadState>(IDLE_DOWNLOAD);
  const [warnings, setWarnings] = useState<SessionRecordingWarnings>(EMPTY_WARNINGS);

  scopeRef.current = scope;
  accountScopeRef.current = accountScope;
  playbackStateRef.current = playback;

  const discardInFlight = useCallback((): void => {
    scopeEpochRef.current += 1;
    playbackGenerationRef.current += 1;
    downloadGenerationRef.current += 1;
    playbackInFlightRef.current = false;
    downloadInFlightRef.current = false;
    const playbackController = playbackControllerRef.current;
    playbackControllerRef.current = null;
    playbackController?.abort();
    const downloadController = downloadControllerRef.current;
    downloadControllerRef.current = null;
    downloadController?.abort();
    positionRef.current = null;
    const audio = audioRef.current;
    if (audio !== null) {
      suppressMediaErrorRef.current = true;
      disposeAudioElement(audio);
    }
  }, []);
  discardRef.current = discardInFlight;

  if (!sameScope(trackedScope, scope)) {
    discardInFlight();
    setTrackedScope(scope);
    setPlayback(IDLE_PLAYBACK);
    setDownload(IDLE_DOWNLOAD);
    setWarnings(EMPTY_WARNINGS);
  }

  const capturePosition = useCallback((): void => {
    const audio = audioRef.current;
    if (audio === null) return;
    const position = readFinitePosition(audio);
    if (position === null) return;
    positionRef.current = position;
  }, []);

  const bindAudio = useCallback((node: HTMLAudioElement | null): void => {
    if (node === null) {
      const current = audioRef.current;
      if (current !== null) {
        suppressMediaErrorRef.current = true;
        disposeAudioElement(current);
      }
      audioRef.current = null;
      return;
    }

    suppressMediaErrorRef.current = false;
    audioRef.current = node;
  }, []);

  const reportMediaError = useCallback((): void => {
    if (suppressMediaErrorRef.current || !scopeRef.current.permitted) return;

    const audio = audioRef.current;
    if (audio !== null) {
      const position = readFinitePosition(audio);
      if (position !== null) positionRef.current = position;
    }

    suppressMediaErrorRef.current = true;
    playbackGenerationRef.current += 1;
    playbackInFlightRef.current = false;
    const controller = playbackControllerRef.current;
    playbackControllerRef.current = null;
    controller?.abort();
    if (audioRef.current !== null) disposeAudioElement(audioRef.current);
    setPlayback({ status: 'media-error' });
  }, []);

  const acceptPlayback = useCallback((generation: number, epoch: number, requestSessionId: string): boolean => {
    const current = scopeRef.current;
    return (
      generation === playbackGenerationRef.current &&
      epoch === scopeEpochRef.current &&
      current.sessionId === requestSessionId &&
      current.permitted &&
      current.accountScope === accountScopeRef.current
    );
  }, []);

  const acceptDownload = useCallback((generation: number, epoch: number, requestSessionId: string): boolean => {
    const current = scopeRef.current;
    return (
      generation === downloadGenerationRef.current &&
      epoch === scopeEpochRef.current &&
      current.sessionId === requestSessionId &&
      current.permitted &&
      current.accountScope === accountScopeRef.current
    );
  }, []);

  const fetchRecording = useCallback(
    (
      purpose: CanvassingSessionRecordingPurpose,
      signal: AbortSignal,
      requestSessionId: string,
    ): Promise<CanvassingSessionRecordingResult> => {
      const override = apiRef.current;
      if (override !== null) {
        return override.fetchRecording({ sessionId: requestSessionId, purpose }, { signal });
      }

      return fetchCanvassingSessionRecording({
        sessionId: requestSessionId,
        purpose,
        signal,
        fetchImpl: fetchImplRef.current,
      }).then(result => result.canvassingSessionRecording);
    },
    [],
  );

  const beginPlayback = useCallback(
    (restorePosition: boolean): void => {
      const requestScope = scopeRef.current;
      if (!requestScope.permitted || playbackInFlightRef.current) return;

      playbackInFlightRef.current = true;
      const generation = ++playbackGenerationRef.current;
      const epoch = scopeEpochRef.current;
      const controller = new AbortController();
      playbackControllerRef.current?.abort();
      playbackControllerRef.current = controller;
      setPlayback({ status: 'loading' });

      const settle = (): void => {
        if (playbackControllerRef.current === controller) playbackControllerRef.current = null;
        playbackInFlightRef.current = false;
      };

      void fetchRecording(CanvassingSessionRecordingPurpose.Playback, controller.signal, requestScope.sessionId)
        .then(result => {
          if (!acceptPlayback(generation, epoch, requestScope.sessionId)) return;
          settle();
          if (controller.signal.aborted) return;

          const interpreted = interpretCanvassingSessionRecording(result);
          if (interpreted.kind === 'ready') {
            setWarnings(current => mergeSessionRecordingWarnings(current, interpreted.warnings));
            setPlayback({
              status: 'ready',
              url: interpreted.url,
              actionId: generation,
              restorePosition,
            });
            return;
          }

          setPlayback({ status: 'error', message: playbackFailureMessage(interpreted) });
        })
        .catch((error: unknown) => {
          if (!acceptPlayback(generation, epoch, requestScope.sessionId)) return;
          settle();
          if (controller.signal.aborted || isAbortError(error)) return;
          setPlayback({ status: 'error', message: SESSION_RECORDING_PLAYBACK_ERROR_MESSAGE });
        });
    },
    [acceptPlayback, fetchRecording],
  );

  const requestPlayback = useCallback((): void => {
    if (!scopeRef.current.permitted) return;

    const current = playbackStateRef.current;
    if (current.status === 'ready' && audioRef.current !== null) {
      audioRef.current.focus();
      attemptPlayback(audioRef.current);
      return;
    }

    beginPlayback(current.status === 'media-error' && positionRef.current !== null);
  }, [beginPlayback]);

  const retryPlayback = useCallback((): void => {
    beginPlayback(positionRef.current !== null);
  }, [beginPlayback]);

  const requestDownload = useCallback((): void => {
    const requestScope = scopeRef.current;
    if (!requestScope.permitted || downloadInFlightRef.current) return;

    downloadInFlightRef.current = true;
    const generation = ++downloadGenerationRef.current;
    const epoch = scopeEpochRef.current;
    const controller = new AbortController();
    downloadControllerRef.current?.abort();
    downloadControllerRef.current = controller;
    setDownload({ status: 'preparing' });

    const settle = (): void => {
      if (downloadControllerRef.current === controller) downloadControllerRef.current = null;
      downloadInFlightRef.current = false;
    };

    void fetchRecording(CanvassingSessionRecordingPurpose.Download, controller.signal, requestScope.sessionId)
      .then(result => {
        if (!acceptDownload(generation, epoch, requestScope.sessionId)) return;
        settle();
        if (controller.signal.aborted) return;

        const interpreted = interpretCanvassingSessionRecording(result);
        if (interpreted.kind !== 'ready') {
          setDownload({ status: 'error', message: downloadFailureMessage(interpreted) });
          return;
        }

        try {
          activateDirectDownload(interpreted.url, canvassingSessionRecordingFileName(requestScope.sessionId));
        } catch {
          setDownload({ status: 'error', message: SESSION_RECORDING_DOWNLOAD_ERROR_MESSAGE });
          return;
        }

        setWarnings(current => mergeSessionRecordingWarnings(current, interpreted.warnings));
        setDownload({ status: 'requested' });
      })
      .catch((error: unknown) => {
        if (!acceptDownload(generation, epoch, requestScope.sessionId)) return;
        settle();
        if (controller.signal.aborted || isAbortError(error)) return;
        setDownload({ status: 'error', message: SESSION_RECORDING_DOWNLOAD_ERROR_MESSAGE });
      });
  }, [acceptDownload, fetchRecording]);

  const readyPlayback = playback.status === 'ready' ? playback : null;

  useEffect(() => {
    if (readyPlayback === null) return undefined;
    const audio = audioRef.current;
    if (audio === null) return undefined;

    const actionId = readyPlayback.actionId;
    const restorePosition = readyPlayback.restorePosition;
    let cancelled = false;
    audio.focus();

    const start = (): void => {
      if (cancelled || actionId !== playbackGenerationRef.current) return;
      if (restorePosition) {
        const position = positionRef.current;
        if (position !== null) restorePlaybackPosition(audio, position);
      }
      audio.focus();
      attemptPlayback(audio);
    };

    if (!restorePosition || audio.readyState >= HAVE_METADATA) {
      start();
      return () => {
        cancelled = true;
      };
    }

    const onMetadata = (): void => start();
    audio.addEventListener('loadedmetadata', onMetadata);
    return () => {
      cancelled = true;
      audio.removeEventListener('loadedmetadata', onMetadata);
    };
  }, [readyPlayback]);

  useEffect(() => {
    return () => {
      discardRef.current();
    };
  }, []);

  return {
    playback,
    download,
    warnings,
    requestPlayback,
    retryPlayback,
    requestDownload,
    reportMediaError,
    capturePosition,
    bindAudio,
  };
};
